import _ from 'platform/utilities/data';
import { SERVICE_CONNECTION_TYPES, BANK_KEYS } from '../../shared/constants';

import {
  getCountryOptions,
  isMilitaryLocation,
  mapCountryNameToCode,
  normalizeAddressLine,
} from '../../shared/utils/contactInformationHelpers';

/**
 * Partially mirrors the v1 prefillRatedDisabilities.
 * The backend prefill sends fetched rated disabilities under the
 * `disabilities` key, which the v1 frontend code renamed to `ratedDisabilities`,
 * with downstream code referencing this latter key.
 * Unlike the v1 version, exclude handling for `view:claimType` (this is specific
 * to a legacy version of the v1 flow).
 *
 * @param {object} data - Prefill form data
 * @returns {object} Form data with `disabilities` renamed to `ratedDisabilities`
 */
export const prefillRatedDisabilities = data => {
  const { disabilities } = data;
  if (!disabilities) return data;

  const serviceConnected = disabilities.filter(
    d => d.decisionCode === SERVICE_CONNECTION_TYPES.serviceConnected,
  );

  const newData = _.omit(['disabilities'], data);

  return serviceConnected.length
    ? _.set('ratedDisabilities', serviceConnected, newData)
    : newData;
};

/**
 * Map the backend prefill `veteran` block into the v2 form's top-level
 * `phoneAndEmail` and `mailingAddress` shape. Mirrors the v1 contact-info
 * prefill (all-claims/prefill-transformer.js) and reuses the shared
 * contact-info helpers (disability-benefits/shared) so both apps stay in sync.
 * @param {object} data - Prefill form data
 * @returns {object} Form data with contact info mapped to the page shape
 */
export const prefillContactInformation = data => {
  const newData = _.omit(['veteran'], data);
  const { veteran } = data;

  if (veteran) {
    const { emailAddress, primaryPhone, mailingAddress } = veteran;
    newData.phoneAndEmail = {};
    if (emailAddress) {
      newData.phoneAndEmail.emailAddress = emailAddress;
    }
    if (primaryPhone) {
      newData.phoneAndEmail.primaryPhone = primaryPhone;
    }
    if (mailingAddress) {
      const countryCode = mapCountryNameToCode(mailingAddress.country);
      const countryOptions = getCountryOptions();
      const livesOnMilitaryBase = isMilitaryLocation(
        mailingAddress.city,
        mailingAddress.state,
      );
      let country = '';
      if (livesOnMilitaryBase) {
        // The card summary only shows state and ZIP when the country is USA.
        country = 'USA';
      } else if (countryOptions.COUNTRY_VALUES.includes(countryCode)) {
        // A country outside the dropdown is cleared so the Veteran has to select one.
        country = countryCode;
      }
      newData.mailingAddress = {
        // strip out any extra data on the prefilled address
        'view:livesOnMilitaryBase': livesOnMilitaryBase,
        country,
        // Normalize address fields at prefill time — trim leading/trailing
        // spaces and collapse consecutive spaces. Prevents the most common
        // source of invalid prefilled data (extra spaces from backend
        // systems). Save-in-progress can also produce un-normalized data,
        // which is handled by ReviewCardField's startInEdit option.
        addressLine1: normalizeAddressLine(mailingAddress.addressLine1) || '',
        addressLine2: normalizeAddressLine(mailingAddress.addressLine2),
        addressLine3: normalizeAddressLine(mailingAddress.addressLine3),
        city: normalizeAddressLine(mailingAddress.city) || '',
        state: mailingAddress.state || '',
        zipCode: mailingAddress.zipCode || '',
      };
    }
  }

  return newData;
};

export const prefillBankInformation = data => {
  const newData = _.omit(BANK_KEYS, data);
  const { bankAccountType, bankAccountNumber, bankRoutingNumber, bankName } =
    data;
  const hasPrefilledBankAccount = !!(
    bankAccountType &&
    bankAccountNumber &&
    bankRoutingNumber
  );
  if (hasPrefilledBankAccount) {
    newData['view:prefilledBankAccount'] = {
      'view:accountType': bankAccountType,
      'view:accountNumber': bankAccountNumber,
      'view:routingNumber': bankRoutingNumber,
      ...(bankName && { 'view:bankName': bankName }),
    };
  }

  newData['view:hasPrefilledBankAccount'] = hasPrefilledBankAccount;

  return newData;
};

const transformations = [
  prefillContactInformation,
  prefillBankInformation,
  prefillRatedDisabilities,
];

const applyTransformations = (data = {}, transformer) => transformer(data);

export default function prefillTransformer(pages, formData, metadata) {
  return {
    metadata,
    formData: transformations.reduce(applyTransformations, formData),
    pages,
  };
}
